// GENERATED by @platform/contracts from specs/platform/main.tsp. Do not edit; run `pnpm generate`.
/* eslint-disable */
import { z } from 'zod';
/** Lifecycle of a platform-admin user in the control-plane pool. */
export const AdminStatus = z.enum(['invited', 'active', 'disabled']);
/** Package identifier (`pkg_...`). */
export const PackageId = z.string().regex(/^pkg_[a-z0-9-]+$/);
export const AdoptPackage = z.object({ packageId: PackageId });
/** A notice staff published (ADR in-app-notices-from-staff): `n_` and a random token. */
export const NoticeId = z.string().regex(/^n_[A-Za-z0-9]{12,40}$/);
/** Tenant identifier. The control plane's own resources use the literal `platform`. */
export const TenantId = z.string().regex(/^[a-z0-9][a-z0-9-]{1,62}$/);
/** Cognito subject (the identity's stable id). */
export const Sub = z.string().min(1).max(128);
/** Where an announcement goes: one tenant (optionally one person in it), or every active tenant of one cell. */
export const AnnouncementTarget = z.object({
    tenantId: TenantId.optional(),
    sub: Sub.optional(),
    cellId: z.string().optional(),
});
/**
 * How much a notice asks of the person. `info` and `important` can be dismissed; a `security`
 * notice stays on screen until the person says they have read it.
 */
export const NoticeSeverity = z.enum(['info', 'important', 'security']);
/** An optional link in a notice, to the page that says more. https only. */
export const NoticeLink = z.object({
    label: z.string().min(1).max(60),
    url: z
        .string()
        .regex(/^https:\/\/[^\s]+$/)
        .max(2000),
});
/** An announcement as the control plane recorded it: who sent it, where, and when. */
export const Announcement = z.object({
    announcementId: NoticeId,
    target: AnnouncementTarget,
    severity: NoticeSeverity,
    title: z.string(),
    body: z.string(),
    link: NoticeLink.optional(),
    publishedAt: z.iso.datetime({ offset: true }),
    expiresAt: z.iso.datetime({ offset: true }).optional(),
    publishedBy: Sub,
    tenantCount: z.int32(),
    withdrawnAt: z.iso.datetime({ offset: true }).optional(),
});
/** What staff say to the people of a tenant or cell (ADR in-app-notices-from-staff). */
export const AnnouncementCreate = z.object({
    target: AnnouncementTarget,
    severity: NoticeSeverity,
    title: z.string().min(1).max(120),
    body: z.string().min(1).max(2000),
    link: NoticeLink.optional(),
    expiresAt: z.iso.datetime({ offset: true }).optional(),
});
/**
 * A kind of AI model a package may use, named by purpose, never by vendor (the assistant context
 * maps it). `standard` is the ceiling (owner ruling 2026-11-03).
 */
export const AssistantModel = z.enum(['fast', 'standard']);
/**
 * What a package allows of the assistant (AI model calls), per tenant (#16). The cell checks
 * every limit BEFORE a call: a call that could cross one is refused and costs nothing. Callers are
 * warned from 80% of a limit (design standard DS-COST-02). Money in millionths of a US dollar;
 * periods are UTC calendar months and days.
 */
export const AssistantAllowance = z.object({
    models: z.array(AssistantModel).min(1),
    monthlyLimitMicroUsd: z.int().min(0),
    dailyLimitMicroUsd: z.int().min(0).optional(),
    memberMonthlyLimitMicroUsd: z.int().min(0).optional(),
});
/** How billing charges a package: per seat (quantity follows memberships) or one flat price. */
export const BillingModel = z.enum(['perSeat', 'flat']);
/** A feature flag a package grants. Closed list, extended by contract change only. */
export const FeatureKey = z.string().regex(/^[a-z][a-z0-9-]*(\.[a-z][a-z0-9-]*)*$/);
/** A catalog entry as a tenant sees it: only packages its tier may adopt. */
export const CatalogEntry = z.object({
    packageId: PackageId,
    name: z.string(),
    features: z.array(FeatureKey),
    version: z.int32(),
});
/** A product's name: its folder under `products/` (`sprocket`, `mymedmetrics`). */
export const ProductName = z.string().regex(/^[a-z][a-z0-9-]*$/);
/** `shared` hosts pool-tier tenants; `dedicated` hosts exactly one silo-tier tenant. */
export const CellMode = z.enum(['shared', 'dedicated']);
/** Isolation tier of a tenant (SaaS Lens: pool = shared cell, silo = dedicated cell). */
export const Tier = z.enum(['pool', 'silo']);
/** A cell as the directory knows it: config from `cells/*.yaml` plus what its first tenant reported. */
export const Cell = z.object({
    cellId: z.string(),
    product: ProductName,
    mode: CellMode,
    tier: Tier,
    region: z.string(),
    hostname: z.string(),
    adminHostname: z.string().optional(),
    issuer: z.string().optional(),
    tenantCount: z.int32().min(0),
});
/** URL-safe workspace slug chosen at signup or by the operator. */
export const Slug = z.string().regex(/^[a-z][a-z0-9-]{2,39}$/);
/** Where a tenant lives: what a shell needs before sign-in. Public; only for provisioned tenants. */
export const DirectoryEntry = z.object({ slug: Slug, cellId: z.string(), hostname: z.string() });
/** Stripe price identifier (`price_...`). */
export const StripePriceId = z.string().regex(/^price_[A-Za-z0-9]+$/);
/**
 * What a tenant is entitled to: an immutable copy of the package as it was when adopted (or
 * last re-applied). Catalog edits never change it.
 */
export const EntitlementSnapshot = z.object({
    packageId: PackageId,
    packageVersion: z.int32(),
    features: z.array(FeatureKey),
    stripePriceId: StripePriceId,
    billingModel: BillingModel.optional(),
    assistant: AssistantAllowance.optional(),
    snapshotAt: z.iso.datetime({ offset: true }),
});
/** Where an export is. `failed` and `expired` are final: ask for a new one. */
export const ExportStatus = z.enum(['pending', 'ready', 'failed', 'expired']);
/** One entry of the platform menu; `group` nests it (one level), `feature` and `action` gate it. */
export const MenuItem = z.object({
    id: z.string(),
    label: z.string(),
    path: z.string().optional(),
    group: z.string().optional(),
    action: z.string().optional(),
    feature: FeatureKey.optional(),
    order: z.int32().min(0),
});
/** One metric summed over a period. */
export const MeteredQuantity = z.object({
    metric: z.string().regex(/^[a-z][a-z0-9-]*(\.[a-z][a-z0-9-]*)+$/),
    unit: z.string(),
    quantity: z.number().min(0),
    records: z.int32().min(0),
});
/** A usage record (SBT meteringEvent shape) — see `platform.metering.recorded`. */
export const MeteringEvent = z.object({
    metric: z.string().regex(/^[a-z][a-z0-9-]*(\.[a-z][a-z0-9-]*)+$/),
    quantity: z.number().min(0),
    unit: z.string(),
    periodStart: z.iso.datetime({ offset: true }),
    periodEnd: z.iso.datetime({ offset: true }),
    dimensions: z.record(z.string(), z.string()),
});
export const MeteringIngest = z.object({
    tenantId: TenantId,
    cellId: z.string(),
    events: z.array(MeteringEvent),
});
export const MeteringReceipt = z.object({ accepted: z.int32().min(0) });
/** A UTC calendar month (`YYYY-MM`) or a UTC day (`YYYY-MM-DD`). */
export const UsagePeriod = z.string().regex(/^[0-9]{4}-[0-9]{2}(-[0-9]{2})?$/);
/** One tenant's metered usage over one period, summed per metric (the metering aggregation). */
export const MeteringUsage = z.object({
    tenantId: TenantId,
    period: UsagePeriod,
    metrics: z.array(MeteredQuantity),
});
/** Lifecycle of a tenant in the registry. */
export const TenantStatus = z.enum([
    'pendingPayment',
    'provisioning',
    'locked',
    'active',
    'suspended',
    'offboarding',
    'deprovisioned',
    'failed',
]);
/** Which onboarding step a tenant is at. Operator view; the public view is `OnboardingStatus`. */
export const OnboardingStage = z.enum(['payment', 'provisioning', 'activated', 'failed']);
/**
 * What a failed onboarding means for the requester. Closed on purpose: clients map each value to
 * fixed product copy, and no internal reason ever reaches a customer (#24).
 */
export const OnboardingIssue = z.enum(['checkoutExpired', 'delayed']);
/** Operator view of a tenant's onboarding workflow. */
export const OnboardingDetail = z.object({
    tenantId: TenantId,
    slug: Slug,
    status: TenantStatus,
    stage: OnboardingStage,
    attempt: z.int32().min(1),
    executionArn: z.string().optional(),
    checkoutSessionId: z.string().optional(),
    checkoutUrl: z.string().optional(),
    failureReason: z.string().optional(),
    retryable: z.boolean().optional(),
    issue: OnboardingIssue.optional(),
    startedAt: z.iso.datetime({ offset: true }),
    updatedAt: z.iso.datetime({ offset: true }),
});
/** A lightweight status view for polling onboarding progress. */
export const OnboardingStatus = z.object({
    slug: Slug,
    status: TenantStatus,
    issue: OnboardingIssue.optional(),
    checkoutUrl: z.string().optional(),
    hostname: z.string().optional(),
});
/** A catalog package. `version` is server-managed and bumps on every edit. */
export const Package = z.object({
    packageId: PackageId,
    product: ProductName,
    name: z.string(),
    tiers: z.array(Tier),
    features: z.array(FeatureKey),
    stripePriceId: StripePriceId,
    billingModel: BillingModel,
    assistant: AssistantAllowance.optional(),
    version: z.int32().min(1),
    retired: z.boolean(),
    createdAt: z.iso.datetime({ offset: true }),
    updatedAt: z.iso.datetime({ offset: true }),
});
export const PackageCreate = z.object({
    packageId: PackageId,
    product: ProductName,
    name: z.string(),
    tiers: z.array(Tier),
    features: z.array(FeatureKey),
    stripePriceId: StripePriceId,
    billingModel: BillingModel.optional(),
    assistant: AssistantAllowance.optional(),
});
/** Any change bumps `version`. `packageId`, `product` and `version` are not editable. */
export const PackageUpdate = z.object({
    name: z.string().optional(),
    tiers: z.array(Tier).optional(),
    features: z.array(FeatureKey).optional(),
    stripePriceId: StripePriceId.optional(),
    billingModel: BillingModel.optional(),
    assistant: z.union([AssistantAllowance, z.null()]).optional(),
});
/**
 * A document a person accepts to use a product (ADR tenant-export-deletion-and-consent). Closed
 * list; a product declares the ones it asks for, with their current versions, in product.yaml.
 */
export const LegalDocumentKind = z.enum(['terms', 'privacy']);
/** A document's version as the product declares it (a date, a number): counsel's label, verbatim. */
export const DocumentVersion = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,31}$/);
/** One acceptance: this version of this document. */
export const DocumentAcceptance = z.object({
    document: LegalDocumentKind,
    version: DocumentVersion,
});
/**
 * How the person chose to receive notices about their account (a breach notice included: the FTC
 * Health Breach Notification Rule allows email only when the person chose it, 16 CFR 318.5(a)(1)).
 */
export const NoticeChannel = z.enum(['email']);
/**
 * A person signing up for a personal product (ADR personal-tenants-sign-up-in-the-cell-pool). Who
 * they are comes from their cell ID token (`Authorization: Bearer`), never from the body.
 */
export const PersonalSignupRequest = z.object({
    packageId: PackageId,
    accepted: z.array(DocumentAcceptance).max(10).optional(),
    noticeChannel: NoticeChannel.optional(),
});
/** A platform-staff user of the control-plane pool (group `PlatformAdmin`, TOTP required). */
export const PlatformAdmin = z.object({
    sub: Sub,
    email: z.string(),
    name: z.string().optional(),
    status: AdminStatus,
    createdAt: z.iso.datetime({ offset: true }),
});
export const PlatformAdminCreate = z.object({ email: z.string(), name: z.string().optional() });
/** What a tenant of a product is: a company (admin, invitations, seats) or one person. */
export const TenantShape = z.enum(['organization', 'personal']);
/** A document a product asks people to accept, at its current version, and where to read it. */
export const LegalDocument = z.object({
    document: LegalDocumentKind,
    version: DocumentVersion,
    url: z.string(),
});
/** A product the platform sells, from `products/<name>/product.yaml`. */
export const Product = z.object({
    name: ProductName,
    displayName: z.string(),
    tenantShape: TenantShape,
    tiers: z.array(Tier),
    legalDocuments: z.array(LegalDocument).optional(),
});
export const SignupRequest = z.object({
    slug: Slug,
    workspaceName: z.string(),
    email: z.string(),
    packageId: PackageId,
});
export const SlugAvailability = z.object({ slug: Slug, available: z.boolean() });
/** Machine-readable error codes. Never say whether a resource exists to a caller outside its tenant. */
export const ErrorCode = z.enum([
    'validation',
    'unauthenticated',
    'forbidden',
    'notFound',
    'conflict',
    'rateLimited',
    'internal',
]);
export const ErrorDetail = z.object({ path: z.string().optional(), message: z.string() });
/** The one error shape. Sent with the matching HTTP status. */
export const ErrorResponse = z.object({
    code: ErrorCode,
    message: z.string(),
    requestId: z.string(),
    details: z.array(ErrorDetail).optional(),
});
/** ULID-style opaque identifier for records. */
export const RecordId = z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/);
/** The registry's record of a deletion: the dates are what is kept once the tenant is gone. */
export const TenantDeletion = z.object({
    requestedAt: z.iso.datetime({ offset: true }),
    purgeAt: z.iso.datetime({ offset: true }),
    cancelledAt: z.iso.datetime({ offset: true }).optional(),
    purgedAt: z.iso.datetime({ offset: true }).optional(),
});
/** A tenant registry entry. Owned by the platform; `cellId` is the directory. */
export const Tenant = z.object({
    tenantId: TenantId,
    slug: Slug,
    product: ProductName,
    name: z.string(),
    tier: Tier,
    status: TenantStatus,
    cellId: z.string(),
    adminEmail: z.string(),
    entitlement: EntitlementSnapshot.optional(),
    flags: z.record(z.string(), z.boolean()),
    deletion: TenantDeletion.optional(),
    createdAt: z.iso.datetime({ offset: true }),
    updatedAt: z.iso.datetime({ offset: true }),
});
/** What the operator supplies to onboard a tenant (sales-led). Self-serve uses `/signup`. */
export const TenantCreate = z.object({
    slug: Slug,
    name: z.string(),
    adminEmail: z.string(),
    tier: Tier,
    packageId: PackageId,
});
export const TenantUpdate = z.object({
    name: z.string().optional(),
    flags: z.record(z.string(), z.boolean()).optional(),
});
export const TierChange = z.object({ tier: Tier, cellId: z.string().optional() });
/** A tier's operational limits. Static per cell; surfaced for the admin console. */
export const TierDefinition = z.object({
    tier: Tier,
    description: z.string(),
    rateLimitPerSecond: z.int32(),
    burstLimit: z.int32(),
    dedicatedCell: z.boolean(),
});
/**
 * A deletion the tenant asked for (ADR tenant-export-deletion-and-consent): everything is purged
 * at `purgeAt` (30 days after the request) unless it is cancelled first.
 */
export const WorkspaceDeletion = z.object({
    requestedAt: z.iso.datetime({ offset: true }),
    purgeAt: z.iso.datetime({ offset: true }),
});
/** The tenant's own view of itself: no other tenant's data, no registry internals. */
export const Workspace = z.object({
    tenantId: TenantId,
    slug: Slug,
    name: z.string(),
    tier: Tier,
    status: TenantStatus,
    entitlement: EntitlementSnapshot.optional(),
    flags: z.record(z.string(), z.boolean()),
    deletion: WorkspaceDeletion.optional(),
});
/** A copy of everything the tenant holds in its cell, as one archive. */
export const WorkspaceExport = z.object({
    exportId: RecordId,
    status: ExportStatus,
    requestedAt: z.iso.datetime({ offset: true }),
    readyAt: z.iso.datetime({ offset: true }).optional(),
    expiresAt: z.iso.datetime({ offset: true }),
    downloadUrl: z.string().optional(),
    downloadExpiresAt: z.iso.datetime({ offset: true }).optional(),
});
/** One validator set per operation, keyed by operationId. Same order as the route manifest. */
export const operations = {
    Admins_list: {
        operationId: 'Admins_list',
        method: 'GET',
        path: '/admins',
        params: z.object({}),
        query: z.object({
            cursor: z.string().optional(),
            limit: z.int32().min(1).max(200).default(50),
        }),
        responses: {
            '200': z.object({
                tenantId: TenantId,
                requestId: z.string(),
                items: z.array(PlatformAdmin),
                nextCursor: z.string().optional(),
            }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Admins_create: {
        operationId: 'Admins_create',
        method: 'POST',
        path: '/admins',
        params: z.object({}),
        query: z.object({}),
        body: PlatformAdminCreate,
        responses: {
            '201': z.object({ tenantId: TenantId, requestId: z.string(), data: PlatformAdmin }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
            '409': ErrorResponse,
        },
    },
    Admins_remove: {
        operationId: 'Admins_remove',
        method: 'DELETE',
        path: '/admins/{sub}',
        params: z.object({ sub: Sub }),
        query: z.object({}),
        responses: {
            '204': z.undefined(),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
            '409': ErrorResponse,
        },
    },
    Admins_get: {
        operationId: 'Admins_get',
        method: 'GET',
        path: '/admins/{sub}',
        params: z.object({ sub: Sub }),
        query: z.object({}),
        responses: {
            '200': z.object({ tenantId: TenantId, requestId: z.string(), data: PlatformAdmin }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Announcements_list: {
        operationId: 'Announcements_list',
        method: 'GET',
        path: '/announcements',
        params: z.object({}),
        query: z.object({
            cursor: z.string().optional(),
            limit: z.int32().min(1).max(200).default(50),
        }),
        responses: {
            '200': z.object({
                tenantId: TenantId,
                requestId: z.string(),
                items: z.array(Announcement),
                nextCursor: z.string().optional(),
            }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Announcements_publish: {
        operationId: 'Announcements_publish',
        method: 'POST',
        path: '/announcements',
        params: z.object({}),
        query: z.object({}),
        body: AnnouncementCreate,
        responses: {
            '201': z.object({ tenantId: TenantId, requestId: z.string(), data: Announcement }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
            '409': ErrorResponse,
        },
    },
    Announcements_withdraw: {
        operationId: 'Announcements_withdraw',
        method: 'POST',
        path: '/announcements/{announcementId}/withdraw',
        params: z.object({ announcementId: NoticeId }),
        query: z.object({}),
        responses: {
            '200': z.object({ tenantId: TenantId, requestId: z.string(), data: Announcement }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Cells_list: {
        operationId: 'Cells_list',
        method: 'GET',
        path: '/cells',
        params: z.object({}),
        query: z.object({}),
        responses: {
            '200': z.object({
                tenantId: TenantId,
                requestId: z.string(),
                items: z.array(Cell),
                nextCursor: z.string().optional(),
            }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Directory_get: {
        operationId: 'Directory_get',
        method: 'GET',
        path: '/directory/{slug}',
        params: z.object({ slug: Slug }),
        query: z.object({}),
        responses: {
            '200': z.object({ tenantId: TenantId, requestId: z.string(), data: DirectoryEntry }),
            '400': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Menu_get: {
        operationId: 'Menu_get',
        method: 'GET',
        path: '/menu',
        params: z.object({}),
        query: z.object({}),
        responses: {
            '200': z.object({
                tenantId: TenantId,
                requestId: z.string(),
                items: z.array(MenuItem),
                nextCursor: z.string().optional(),
            }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Metering_ingest: {
        operationId: 'Metering_ingest',
        method: 'POST',
        path: '/metering',
        params: z.object({}),
        query: z.object({}),
        body: MeteringIngest,
        responses: {
            '202': z.object({ tenantId: TenantId, requestId: z.string(), data: MeteringReceipt }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Metering_usage: {
        operationId: 'Metering_usage',
        method: 'GET',
        path: '/metering/usage',
        params: z.object({}),
        query: z.object({
            cursor: z.string().optional(),
            limit: z.int32().min(1).max(200).default(50),
            period: UsagePeriod,
            tenantId: TenantId.optional(),
        }),
        responses: {
            '200': z.object({
                tenantId: TenantId,
                requestId: z.string(),
                items: z.array(MeteringUsage),
                nextCursor: z.string().optional(),
            }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Packages_list: {
        operationId: 'Packages_list',
        method: 'GET',
        path: '/packages',
        params: z.object({}),
        query: z.object({
            cursor: z.string().optional(),
            limit: z.int32().min(1).max(200).default(50),
            includeRetired: z.boolean().optional(),
        }),
        responses: {
            '200': z.object({
                tenantId: TenantId,
                requestId: z.string(),
                items: z.array(Package),
                nextCursor: z.string().optional(),
            }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Packages_create: {
        operationId: 'Packages_create',
        method: 'POST',
        path: '/packages',
        params: z.object({}),
        query: z.object({}),
        body: PackageCreate,
        responses: {
            '201': z.object({ tenantId: TenantId, requestId: z.string(), data: Package }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
            '409': ErrorResponse,
        },
    },
    Packages_get: {
        operationId: 'Packages_get',
        method: 'GET',
        path: '/packages/{packageId}',
        params: z.object({ packageId: PackageId }),
        query: z.object({}),
        responses: {
            '200': z.object({ tenantId: TenantId, requestId: z.string(), data: Package }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Packages_update: {
        operationId: 'Packages_update',
        method: 'PATCH',
        path: '/packages/{packageId}',
        params: z.object({ packageId: PackageId }),
        query: z.object({}),
        body: PackageUpdate,
        responses: {
            '200': z.object({ tenantId: TenantId, requestId: z.string(), data: Package }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Packages_retire: {
        operationId: 'Packages_retire',
        method: 'POST',
        path: '/packages/{packageId}/retire',
        params: z.object({ packageId: PackageId }),
        query: z.object({}),
        responses: {
            '200': z.object({ tenantId: TenantId, requestId: z.string(), data: Package }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
            '409': ErrorResponse,
        },
    },
    Products_list: {
        operationId: 'Products_list',
        method: 'GET',
        path: '/products',
        params: z.object({}),
        query: z.object({}),
        responses: {
            '200': z.object({
                tenantId: TenantId,
                requestId: z.string(),
                items: z.array(Product),
                nextCursor: z.string().optional(),
            }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Signup_request: {
        operationId: 'Signup_request',
        method: 'POST',
        path: '/signup',
        params: z.object({}),
        query: z.object({}),
        body: SignupRequest,
        responses: {
            '202': z.object({ tenantId: TenantId, requestId: z.string(), data: OnboardingStatus }),
            '400': ErrorResponse,
            '409': ErrorResponse,
        },
    },
    Signup_availability: {
        operationId: 'Signup_availability',
        method: 'GET',
        path: '/signup/availability',
        params: z.object({}),
        query: z.object({ slug: Slug }),
        responses: {
            '200': z.object({ tenantId: TenantId, requestId: z.string(), data: SlugAvailability }),
            '400': ErrorResponse,
        },
    },
    Signup_catalog: {
        operationId: 'Signup_catalog',
        method: 'GET',
        path: '/signup/catalog',
        params: z.object({}),
        query: z.object({ product: ProductName.optional() }),
        responses: {
            '200': z.object({
                tenantId: TenantId,
                requestId: z.string(),
                items: z.array(CatalogEntry),
                nextCursor: z.string().optional(),
            }),
            '400': ErrorResponse,
        },
    },
    Signup_documents: {
        operationId: 'Signup_documents',
        method: 'GET',
        path: '/signup/documents',
        params: z.object({}),
        query: z.object({ product: ProductName }),
        responses: {
            '200': z.object({
                tenantId: TenantId,
                requestId: z.string(),
                items: z.array(LegalDocument),
                nextCursor: z.string().optional(),
            }),
            '400': ErrorResponse,
        },
    },
    Signup_personal: {
        operationId: 'Signup_personal',
        method: 'POST',
        path: '/signup/personal',
        params: z.object({}),
        query: z.object({}),
        body: PersonalSignupRequest,
        responses: {
            '202': z.object({ tenantId: TenantId, requestId: z.string(), data: OnboardingStatus }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '409': ErrorResponse,
        },
    },
    Signup_status: {
        operationId: 'Signup_status',
        method: 'GET',
        path: '/signup/status',
        params: z.object({}),
        query: z.object({ slug: Slug }),
        responses: {
            '200': z.object({ tenantId: TenantId, requestId: z.string(), data: OnboardingStatus }),
            '400': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Tenants_list: {
        operationId: 'Tenants_list',
        method: 'GET',
        path: '/tenants',
        params: z.object({}),
        query: z.object({
            cursor: z.string().optional(),
            limit: z.int32().min(1).max(200).default(50),
            status: TenantStatus.optional(),
        }),
        responses: {
            '200': z.object({
                tenantId: TenantId,
                requestId: z.string(),
                items: z.array(Tenant),
                nextCursor: z.string().optional(),
            }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Tenants_create: {
        operationId: 'Tenants_create',
        method: 'POST',
        path: '/tenants',
        params: z.object({}),
        query: z.object({}),
        body: TenantCreate,
        responses: {
            '202': z.object({ tenantId: TenantId, requestId: z.string(), data: Tenant }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
            '409': ErrorResponse,
        },
    },
    Tenants_get: {
        operationId: 'Tenants_get',
        method: 'GET',
        path: '/tenants/{tenantId}',
        params: z.object({ tenantId: TenantId }),
        query: z.object({}),
        responses: {
            '200': z.object({ tenantId: TenantId, requestId: z.string(), data: Tenant }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Tenants_update: {
        operationId: 'Tenants_update',
        method: 'PATCH',
        path: '/tenants/{tenantId}',
        params: z.object({ tenantId: TenantId }),
        query: z.object({}),
        body: TenantUpdate,
        responses: {
            '200': z.object({ tenantId: TenantId, requestId: z.string(), data: Tenant }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Tenants_offboard: {
        operationId: 'Tenants_offboard',
        method: 'POST',
        path: '/tenants/{tenantId}/offboard',
        params: z.object({ tenantId: TenantId }),
        query: z.object({}),
        responses: {
            '202': z.object({ tenantId: TenantId, requestId: z.string(), data: Tenant }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
            '409': ErrorResponse,
        },
    },
    Tenants_onboarding: {
        operationId: 'Tenants_onboarding',
        method: 'GET',
        path: '/tenants/{tenantId}/onboarding',
        params: z.object({ tenantId: TenantId }),
        query: z.object({}),
        responses: {
            '200': z.object({ tenantId: TenantId, requestId: z.string(), data: OnboardingDetail }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Tenants_adoptPackage: {
        operationId: 'Tenants_adoptPackage',
        method: 'POST',
        path: '/tenants/{tenantId}/package',
        params: z.object({ tenantId: TenantId }),
        query: z.object({}),
        body: AdoptPackage,
        responses: {
            '200': z.object({ tenantId: TenantId, requestId: z.string(), data: Tenant }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
            '409': ErrorResponse,
        },
    },
    Tenants_reapplyPackage: {
        operationId: 'Tenants_reapplyPackage',
        method: 'POST',
        path: '/tenants/{tenantId}/package/reapply',
        params: z.object({ tenantId: TenantId }),
        query: z.object({}),
        responses: {
            '200': z.object({ tenantId: TenantId, requestId: z.string(), data: Tenant }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
            '409': ErrorResponse,
        },
    },
    Tenants_resume: {
        operationId: 'Tenants_resume',
        method: 'POST',
        path: '/tenants/{tenantId}/resume',
        params: z.object({ tenantId: TenantId }),
        query: z.object({}),
        responses: {
            '200': z.object({ tenantId: TenantId, requestId: z.string(), data: Tenant }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
            '409': ErrorResponse,
        },
    },
    Tenants_retry: {
        operationId: 'Tenants_retry',
        method: 'POST',
        path: '/tenants/{tenantId}/retry',
        params: z.object({ tenantId: TenantId }),
        query: z.object({}),
        responses: {
            '202': z.object({ tenantId: TenantId, requestId: z.string(), data: Tenant }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
            '409': ErrorResponse,
        },
    },
    Tenants_suspend: {
        operationId: 'Tenants_suspend',
        method: 'POST',
        path: '/tenants/{tenantId}/suspend',
        params: z.object({ tenantId: TenantId }),
        query: z.object({}),
        responses: {
            '200': z.object({ tenantId: TenantId, requestId: z.string(), data: Tenant }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
            '409': ErrorResponse,
        },
    },
    Tenants_changeTier: {
        operationId: 'Tenants_changeTier',
        method: 'POST',
        path: '/tenants/{tenantId}/tier',
        params: z.object({ tenantId: TenantId }),
        query: z.object({}),
        body: TierChange,
        responses: {
            '202': z.object({ tenantId: TenantId, requestId: z.string(), data: Tenant }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
            '409': ErrorResponse,
        },
    },
    Tiers_list: {
        operationId: 'Tiers_list',
        method: 'GET',
        path: '/tiers',
        params: z.object({}),
        query: z.object({}),
        responses: {
            '200': z.object({
                tenantId: TenantId,
                requestId: z.string(),
                items: z.array(TierDefinition),
                nextCursor: z.string().optional(),
            }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Workspaces_get: {
        operationId: 'Workspaces_get',
        method: 'GET',
        path: '/workspace',
        params: z.object({}),
        query: z.object({}),
        responses: {
            '200': z.object({ tenantId: TenantId, requestId: z.string(), data: Workspace }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Workspaces_catalog: {
        operationId: 'Workspaces_catalog',
        method: 'GET',
        path: '/workspace/catalog',
        params: z.object({}),
        query: z.object({}),
        responses: {
            '200': z.object({
                tenantId: TenantId,
                requestId: z.string(),
                items: z.array(CatalogEntry),
                nextCursor: z.string().optional(),
            }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    WorkspaceDeletions_cancel: {
        operationId: 'WorkspaceDeletions_cancel',
        method: 'DELETE',
        path: '/workspace/deletion',
        params: z.object({}),
        query: z.object({}),
        responses: {
            '204': z.undefined(),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
            '409': ErrorResponse,
        },
    },
    WorkspaceDeletions_request: {
        operationId: 'WorkspaceDeletions_request',
        method: 'POST',
        path: '/workspace/deletion',
        params: z.object({}),
        query: z.object({}),
        responses: {
            '202': z.object({ tenantId: TenantId, requestId: z.string(), data: WorkspaceDeletion }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
            '409': ErrorResponse,
        },
    },
    Workspaces_entitlement: {
        operationId: 'Workspaces_entitlement',
        method: 'GET',
        path: '/workspace/entitlement',
        params: z.object({}),
        query: z.object({}),
        responses: {
            '200': z.object({ tenantId: TenantId, requestId: z.string(), data: EntitlementSnapshot }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    WorkspaceExports_create: {
        operationId: 'WorkspaceExports_create',
        method: 'POST',
        path: '/workspace/exports',
        params: z.object({}),
        query: z.object({}),
        responses: {
            '202': z.object({ tenantId: TenantId, requestId: z.string(), data: WorkspaceExport }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
            '409': ErrorResponse,
        },
    },
    WorkspaceExports_get: {
        operationId: 'WorkspaceExports_get',
        method: 'GET',
        path: '/workspace/exports/{exportId}',
        params: z.object({ exportId: RecordId }),
        query: z.object({}),
        responses: {
            '200': z.object({ tenantId: TenantId, requestId: z.string(), data: WorkspaceExport }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
};
//# sourceMappingURL=schemas.js.map